/**
 * A single material calculator.
 * The first calculation is counted towards the daily limit; after that the result
 * updates live as inputs change, without using more of the limit.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { EmptyState, IconTile, NumberField, Page, PageHeader, SectionCard } from '../ui';
import { useLanguage } from '../../utils/LanguageContext';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import UsageAlertBanner from '../gate/UsageAlertBanner';
import { UNIT_OPTIONS, calculateWithRange } from '../../utils/materialEstimatorData';
import { formatNumber } from '../../utils/format';
import { CATEGORY_ICONS, addRecentCalculator, findCalculator } from './calculatorMeta';
import { EnglishBondSVG, StretcherBondSVG } from './MaterialIllustration';

const text = {
  en: {
    back: 'Calculators',
    inputs: 'Measurements',
    calculate: 'Calculate',
    reset: 'Reset',
    youNeed: 'You will need about',
    range: 'Range',
    alsoNeeded: 'Also needed',
    howCalculated: 'How this is calculated',
    liveNote: 'Results update as you change the measurements.',
    fillIn: 'Fill in the measurements, then tap Calculate.',
    notFound: 'Calculator not found',
    notFoundDesc: 'It may have been renamed. Find it in the calculators list.',
    stretcherTitle: 'Stretcher bond (4.5" wall)',
    stretcherDesc: 'Half-brick wall, bricks laid lengthwise. About 5 bricks per sq ft.',
    englishTitle: 'English bond (9" wall)',
    englishDesc: 'Full-brick wall, alternating stretcher and header courses. About 10 bricks per sq ft.',
  },
  my: {
    back: 'Kalkulator',
    inputs: 'Ukuran',
    calculate: 'Kira',
    reset: 'Set semula',
    youNeed: 'Anda perlukan kira-kira',
    range: 'Julat',
    alsoNeeded: 'Juga diperlukan',
    howCalculated: 'Cara pengiraan',
    liveNote: 'Keputusan dikemas kini apabila anda menukar ukuran.',
    fillIn: 'Isi ukuran, kemudian tekan Kira.',
    notFound: 'Kalkulator tidak dijumpai',
    notFoundDesc: 'Ia mungkin telah ditukar nama. Cari dalam senarai kalkulator.',
    stretcherTitle: 'Ikatan stretcher (dinding 4.5")',
    stretcherDesc: 'Dinding separuh bata, bata diletak memanjang. Kira-kira 5 bata sekaki persegi.',
    englishTitle: 'Ikatan Inggeris (dinding 9")',
    englishDesc: 'Dinding bata penuh, berselang stretcher dan header. Kira-kira 10 bata sekaki persegi.',
  },
};

const UNIT_SHORT = { ft: 'ft', inch: 'in', m: 'm', cm: 'cm', mm: 'mm', sqft: 'sq ft', sqm: 'm²', sqin: 'sq in' };

/** Starting unit for each unit type = the unit the calculator's own inputs are written in */
const defaultUnitsFor = (calc) => {
  const units = {};
  calc.inputs.forEach((input) => {
    if (input.unitType && !units[input.unitType]) {
      const known = UNIT_OPTIONS[input.unitType]?.some((u) => u.id === input.unit);
      units[input.unitType] = known ? input.unit : UNIT_OPTIONS[input.unitType]?.[0]?.id;
    }
  });
  return units;
};

const defaultValuesFor = (calc) =>
  Object.fromEntries(calc.inputs.filter((i) => i.default !== undefined).map((i) => [i.id, String(i.default)]));

const BOND_ILLUSTRATIONS = {
  bricks: { Svg: StretcherBondSVG, title: 'stretcherTitle', desc: 'stretcherDesc' },
  bricksByArea: { Svg: StretcherBondSVG, title: 'stretcherTitle', desc: 'stretcherDesc' },
  bricksFullWall: { Svg: EnglishBondSVG, title: 'englishTitle', desc: 'englishDesc' },
};

const CalculatorPage = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const my = language === 'my';
  const t = text[language] || text.en;
  const calc = useMemo(() => findCalculator(materialId), [materialId]);
  const { checkAccess, GateModalComponent } = useGate();

  const [values, setValues] = useState(() => (calc ? defaultValuesFor(calc) : {}));
  const [units, setUnits] = useState(() => (calc ? defaultUnitsFor(calc) : {}));
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (!calc) return;
    addRecentCalculator(calc.id);
    setValues(defaultValuesFor(calc));
    setUnits(defaultUnitsFor(calc));
    setUnlocked(false);
  }, [calc]);

  const isValid = useMemo(
    () =>
      !!calc &&
      calc.inputs.every((input) => {
        const raw = values[input.id];
        if (input.default !== undefined && (raw === undefined || raw === '')) return true;
        const v = parseFloat(raw);
        return input.default !== undefined ? v >= 0 : v > 0;
      }),
    [calc, values]
  );

  const result = useMemo(() => {
    if (!calc || !unlocked || !isValid) return null;
    const parsed = {};
    calc.inputs.forEach((input) => {
      const v = parseFloat(values[input.id]);
      parsed[input.id] = Number.isNaN(v) ? input.default || 0 : v;
    });
    const out = calc.calculate(parsed, units);
    const withRange = (x) => ({ ...x, ...calculateWithRange(x.value, x.unit) });
    return { primary: withRange(out.primary), secondary: (out.secondary || []).map(withRange) };
  }, [calc, unlocked, isValid, values, units]);

  if (!calc) {
    return (
      <Page>
        <PageHeader title="" onBack={() => navigate('/app/calculators')} backLabel={t.back} />
        <EmptyState
          icon={<SearchOffRoundedIcon />}
          title={t.notFound}
          description={t.notFoundDesc}
          action={<Button variant="contained" onClick={() => navigate('/app/calculators')}>{t.back}</Button>}
        />
      </Page>
    );
  }

  const handleCalculate = () => {
    if (!isValid) return;
    if (!checkAccess(GATE_ACTIONS.MATERIAL_ESTIMATE)) return;
    setUnlocked(true);
  };

  const handleReset = () => {
    setValues(defaultValuesFor(calc));
    setUnits(defaultUnitsFor(calc));
  };

  const bond = BOND_ILLUSTRATIONS[calc.id];
  const selectableUnits = !!calc.unitSelectable;

  return (
    <Page maxWidth="lg">
      <PageHeader
        overline={my ? calc.category.labelMy : calc.category.label}
        title={my ? calc.labelMy : calc.label}
        onBack={() => navigate('/app/calculators')}
        backLabel={t.back}
      />

      <UsageAlertBanner actionCode={GATE_ACTIONS.MATERIAL_ESTIMATE} language={language} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          {bond && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <bond.Svg />
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  {t[bond.title]}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t[bond.desc]}
                </Typography>
              </CardContent>
            </Card>
          )}

          <SectionCard
            title={t.inputs}
            icon={CATEGORY_ICONS[calc.categoryId]}
            action={
              <Button size="small" startIcon={<RestartAltRoundedIcon />} onClick={handleReset} sx={{ color: 'text.secondary' }}>
                {t.reset}
              </Button>
            }
          >
            <Stack spacing={2}>
              {calc.inputs.map((input) => {
                const label = my ? input.labelMy : input.label;
                const hasUnitChoice = selectableUnits && input.unitType && UNIT_OPTIONS[input.unitType];
                return (
                  <NumberField
                    key={input.id}
                    label={label}
                    value={values[input.id] ?? ''}
                    onChange={(v) => setValues((prev) => ({ ...prev, [input.id]: v }))}
                    placeholder={input.default !== undefined ? String(input.default) : undefined}
                    units={hasUnitChoice ? UNIT_OPTIONS[input.unitType].map((u) => ({ id: u.id, label: UNIT_SHORT[u.id] || u.id })) : undefined}
                    unitValue={hasUnitChoice ? units[input.unitType] : undefined}
                    onUnitChange={(u) => setUnits((prev) => ({ ...prev, [input.unitType]: u }))}
                    unit={hasUnitChoice ? undefined : (my ? input.unitMy : input.unit) || undefined}
                  />
                );
              })}
            </Stack>
            {!unlocked && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<CalculateRoundedIcon />}
                onClick={handleCalculate}
                disabled={!isValid}
                sx={{ mt: 2.5 }}
              >
                {t.calculate}
              </Button>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          {result ? (
            <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
              <Card sx={{ mb: 2, borderColor: 'primary.main', borderWidth: 1.5 }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography variant="overline" color="text.secondary">
                    {t.youNeed}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Typography className="tabular" sx={{ fontSize: { xs: '2.25rem', sm: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                      {formatNumber(result.primary.avg)}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {my ? result.primary.unitMy : result.primary.unit}
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                    {my ? result.primary.labelMy : result.primary.label}
                  </Typography>
                  {result.primary.extra && (
                    <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                      = {my ? result.primary.extraMy : result.primary.extra}
                    </Typography>
                  )}
                  {result.primary.min !== result.primary.max && (
                    <Chip
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1.5 }}
                      className="tabular"
                      label={`${t.range}: ${formatNumber(result.primary.min)} – ${formatNumber(result.primary.max)}`}
                    />
                  )}

                  {result.secondary.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        {t.alsoNeeded}
                      </Typography>
                      <Stack divider={<Divider flexItem />} spacing={1.25}>
                        {result.secondary.map((item) => (
                          <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {my ? item.labelMy : item.label}
                              </Typography>
                              {item.extra && (
                                <Typography variant="caption" color="text.secondary">
                                  = {my ? item.extraMy : item.extra}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography variant="body2" fontWeight={700} className="tabular">
                                {formatNumber(item.avg)} {my ? item.unitMy : item.unit}
                              </Typography>
                              {item.min !== item.max && (
                                <Typography variant="caption" color="text.secondary" className="tabular">
                                  {formatNumber(item.min)} – {formatNumber(item.max)}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2, px: 0.5 }}>
                {t.liveNote}
              </Typography>
            </Box>
          ) : (
            <Card sx={{ mb: 2, borderStyle: 'dashed' }}>
              <CardContent sx={{ py: 5, textAlign: 'center' }}>
                <IconTile icon={<CalculateRoundedIcon />} size={48} sx={{ mx: 'auto', mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {t.fillIn}
                </Typography>
              </CardContent>
            </Card>
          )}

          {calc.assumptions && (
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="subtitle2">{t.howCalculated}</Typography>
              <Typography variant="body2">{my ? calc.assumptions.my : calc.assumptions.en}</Typography>
            </Alert>
          )}
        </Grid>
      </Grid>

      <GateModalComponent />
    </Page>
  );
};

export default CalculatorPage;
